import React, { useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Grid,
  Typography,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info"; // Import InfoIcon from Material-UI
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux/hooks/redux-hooks";
import {
  fetchParticularUser,
  resetParticularUser,
} from "../../../redux/actions/UserActions";
import { displayDateFormart } from "../../../utility/utils";
import Divider from "../../Divider";

type Props = {
  projectId: number;
};

type ApprovalProps = {
  approver?: string;
  role?: number;
  isBu?: boolean;
  isRejected?: boolean;
  isOnHolded?: boolean | null | undefined;
  isApproved?: boolean | null | undefined;
  date?: Date | null | undefined;
  isCreated?: boolean;
  isSubmitted?: boolean;
  isDrafted?: boolean;
  isPreviousApprovalPending?: boolean;
  rejectedComment?: string;
  buapprovedComment?: string;
  sbuapprovedComment?: string;
  sbgapprovedComment?: string;
  csgoapprovedComment?: string;
};

const buHead = "BU Head";
const sbuHead = "SBU Head";
const sbgHead = "SBG Head";
const corporate = "Corporate";

const ApprovalInfo = ({
  approver,
  role,
  isBu,
  isRejected,
  isOnHolded,
  isApproved,
  date,
  isCreated,
  isSubmitted,
  isDrafted,
  isPreviousApprovalPending,
  rejectedComment,
  buapprovedComment,
  sbuapprovedComment,
  sbgapprovedComment,
  csgoapprovedComment,
}: ApprovalProps) => {
  const info = isRejected
    ? ["error.main", "Rejected"]
    : isOnHolded
    ? ["warning.main", "On-Hold"]
    : isApproved
    ? ["success.main", "Approved"]
    : isPreviousApprovalPending
    ? ["warning.main", "Pending for previous approval"]
    : ["warning.main", "Pending for approval"];

  const getColor = () => {
    if (isSubmitted || isCreated || isDrafted) {
      return "info.main";
    }
    return info[0];
  };

  const getStatus = () => {
    if (isSubmitted) return "Submitted";
    if (isCreated) return "Created";
    if (isDrafted) return "Pending for submission";
    return info[1];
  };

  const approvedCommments = () => {
    if (isApproved) {
      if (role === 3 && !isBu) {
        return sbuapprovedComment || "No comments";
      }
      if (role === 3 && isBu) {
        return buapprovedComment || "No comments";
      }
      if (role === 4) {
        return sbgapprovedComment || "No comments";
      }
      if (role === 5) {
        return csgoapprovedComment || "No comments";
      }
    }
  };

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={4}>
        <Typography color={getColor()} variant="subtitle2">
          {approver}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography color={getColor()} variant="subtitle2">
          {getStatus()}
          {isRejected && (
            <Tooltip
              title={rejectedComment ? rejectedComment : "No comments"}
              placement="right-end"
            >
              <InfoIcon
                fontSize="small"
                color="info"
                sx={{ paddingLeft: "5px" }}
              />
            </Tooltip>
          )}

          {isApproved && (
            <Tooltip title={approvedCommments()} placement="right-end">
              <InfoIcon
                fontSize="small"
                color="info"
                sx={{ paddingLeft: "5px" }}
              />
            </Tooltip>
          )}
        </Typography>
      </Grid>
      {date && (
        <Grid item xs={4}>
          <Typography color={getColor()} variant="subtitle2">
            {displayDateFormart(date)}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

const ApprovalStatusSection = ({ projectId }: Props) => {
  const dispatch = useAppDispatch();

  const particularProjectResponse = useAppSelector((state) =>
    state.projectResponse.project_Response.find(
      (rec) => rec.projectId === projectId
    )
  );
  const rejectedUserData = useAppSelector(
    (state) => state.user.particular_User
  );

  useEffect(() => {
    if (particularProjectResponse?.rejectedUserID) {
      dispatch(fetchParticularUser(particularProjectResponse?.rejectedUserID));
    } else {
      dispatch(resetParticularUser());
    }
  }, [particularProjectResponse, dispatch]);

  const rejectedBy = useMemo(() => {
    if (!rejectedUserData.roleId) {
      return "";
    }
    if (
      particularProjectResponse?.isBUApprovalRequired &&
      !particularProjectResponse.isBUApproved
    ) {
      return buHead;
    }

    if (rejectedUserData.roleId === 3) {
      return sbuHead;
    } else if (rejectedUserData.roleId === 4) {
      return sbgHead;
    } else {
      return corporate;
    }
  }, [rejectedUserData, particularProjectResponse]);

  return (
    <>
      <Card sx={{ margin: "5px" }}>
        <Accordion disableGutters>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              paddingLeft: "10px",
              paddingRight: "10px",
            }}
          >
            Approval Status
          </AccordionSummary>
          <Divider />
          <AccordionDetails
            sx={{ paddingTop: 0, paddingBottom: 0, paddingLeft: "1.5em" }}
          >
            {particularProjectResponse?.bdPoCName &&
              particularProjectResponse?.createdDate && (
                <ApprovalInfo
                  isCreated={true}
                  isSubmitted={false}
                  role={1}
                  approver={particularProjectResponse?.bdPoCName}
                  date={particularProjectResponse?.createdDate}
                />
              )}
            {!particularProjectResponse?.bdPoCName &&
              particularProjectResponse?.bdHeadName &&
              particularProjectResponse?.createdDate && (
                <ApprovalInfo
                  isCreated={true}
                  isSubmitted={false}
                  role={2}
                  approver={particularProjectResponse?.bdHeadName}
                  date={particularProjectResponse?.createdDate}
                />
              )}
            {particularProjectResponse?.bdHeadName &&
              particularProjectResponse?.isDrafted &&
              !particularProjectResponse?.submittedDate && (
                <ApprovalInfo
                  isDrafted={true}
                  approver={particularProjectResponse?.bdHeadName}
                />
              )}
            {particularProjectResponse?.bdHeadName &&
              particularProjectResponse?.submittedDate && (
                <ApprovalInfo
                  isCreated={false}
                  isSubmitted={true}
                  approver={particularProjectResponse?.bdHeadName}
                  date={particularProjectResponse?.submittedDate}
                />
              )}
            {particularProjectResponse?.isBUApprovalRequired && (
              <ApprovalInfo
                rejectedComment={particularProjectResponse?.rejectedComment}
                buapprovedComment={particularProjectResponse?.buapprovedComment}
                approver={particularProjectResponse?.buUserName}
                isRejected={rejectedBy === buHead}
                role={3}
                isBu={true}
                isOnHolded={particularProjectResponse?.isBuonHold}
                isApproved={particularProjectResponse?.isBUApproved}
                isDrafted={
                  particularProjectResponse?.isDrafted &&
                  !particularProjectResponse?.isSubmitted
                }
                date={
                  rejectedBy === buHead
                    ? particularProjectResponse?.rejectedDate
                    : particularProjectResponse?.isBuonHold
                    ? particularProjectResponse?.buonHoldDate
                    : particularProjectResponse?.buapprovedDate
                }
              />
            )}
            {rejectedBy !== buHead && (
              <>
                <ApprovalInfo
                  rejectedComment={particularProjectResponse?.rejectedComment}
                  sbuapprovedComment={
                    particularProjectResponse?.sbuapprovedComment
                  }
                  approver={particularProjectResponse?.sbuUserName}
                  isRejected={rejectedBy === sbuHead}
                  role={3}
                  isOnHolded={
                    particularProjectResponse?.isSbuonHold ||
                    particularProjectResponse?.isBuonHold
                  }
                  isDrafted={
                    particularProjectResponse?.isDrafted &&
                    !particularProjectResponse?.isSubmitted
                  }
                  isApproved={particularProjectResponse?.isSBUApproved}
                  isPreviousApprovalPending={
                    particularProjectResponse?.isBUApprovalRequired &&
                    !particularProjectResponse?.isBUApproved
                  }
                  date={
                    rejectedBy === sbuHead
                      ? particularProjectResponse?.rejectedDate
                      : particularProjectResponse?.isSbuonHold
                      ? particularProjectResponse?.sbuonHoldDate
                      : particularProjectResponse?.sbuapprovedDate
                  }
                />
                {rejectedBy !== sbuHead && (
                  <>
                    <ApprovalInfo
                      rejectedComment={
                        particularProjectResponse?.rejectedComment
                      }
                      sbgapprovedComment={
                        particularProjectResponse?.sbgapprovedComment
                      }
                      approver={particularProjectResponse?.sbgUserName}
                      isRejected={rejectedBy === sbgHead}
                      role={4}
                      isOnHolded={
                        particularProjectResponse?.isSbgonHold ||
                        particularProjectResponse?.isBuonHold ||
                        particularProjectResponse?.isSbuonHold
                      }
                      isDrafted={
                        particularProjectResponse?.isDrafted &&
                        !particularProjectResponse?.isSubmitted
                      }
                      isApproved={particularProjectResponse?.isSBGApproved}
                      isPreviousApprovalPending={
                        !particularProjectResponse?.isSBUApproved
                      }
                      date={
                        rejectedBy === sbgHead
                          ? particularProjectResponse?.rejectedDate
                          : particularProjectResponse?.isSbgonHold
                          ? particularProjectResponse?.sbgonHoldDate
                          : particularProjectResponse?.sbgapprovedDate
                      }
                    />
                    {(!particularProjectResponse?.isBaseLoad ||
                      !particularProjectResponse?.isGreenListCustomer || particularProjectResponse?.enforceCsgoApproval) &&
                      rejectedBy !== sbgHead && (
                        <ApprovalInfo
                          rejectedComment={
                            particularProjectResponse?.rejectedComment
                          }
                          csgoapprovedComment={
                            particularProjectResponse?.csgoapprovedComment
                          }
                          approver={particularProjectResponse?.csgoUserName}
                          isRejected={rejectedBy === corporate}
                          role={5}
                          isOnHolded={
                            particularProjectResponse?.isCsgoonHold ||
                            particularProjectResponse?.isSbgonHold ||
                            particularProjectResponse?.isBuonHold ||
                            particularProjectResponse?.isSbuonHold
                          }
                          isDrafted={
                            particularProjectResponse?.isDrafted &&
                            !particularProjectResponse?.isSubmitted
                          }
                          isApproved={
                            particularProjectResponse?.isCSGOApproved!
                          }
                          isPreviousApprovalPending={
                            !particularProjectResponse?.isSBGApproved
                          }
                          date={
                            rejectedBy === corporate
                              ? particularProjectResponse?.rejectedDate
                              : particularProjectResponse?.isCsgoonHold
                              ? particularProjectResponse?.csgoonHoldDate
                              : particularProjectResponse?.csgoapprovedDate
                          }
                        />
                      )}
                  </>
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </Card>
    </>
  );
};

export default ApprovalStatusSection;
